import { Anchor, Close, Content, Portal, Root, Trigger } from '@radix-ui/react-popover';
import clsx from 'clsx';

export { Anchor, Close, Content, Portal, Root, Trigger };

import type { ComponentProps, FunctionComponent, ReactNode, Ref } from 'react';

import { useColorSchemeScope } from '../color-scheme/ColorSchemeScope';
import { floatingSurfaceClassNames, floatingSurfaceItemClassNames } from '../surface/surfaceClasses';

export type PopoverProps = {
    readonly children: ReactNode;
    readonly defaultOpen?: boolean;
    readonly modal?: boolean;
    readonly onOpenChange?: (open: boolean) => void;
    readonly open?: boolean;
};

export type PopoverContentProps = ComponentProps<typeof Content> & {
    readonly onOpenAutoFocus?: (event: Event) => void;
};

export type PopoverTriggerProps = {
    readonly asChild?: boolean;
    readonly children: ReactNode;
    readonly className?: string;
};

export type PopoverAnchorProps = {
    readonly asChild?: boolean;
    readonly children: ReactNode;
    readonly className?: string;
    readonly ref?: Ref<HTMLDivElement>;
};

export type PopoverCloseProps = {
    readonly asChild?: boolean;
    readonly children?: ReactNode;
    readonly className?: string;
};

export const StyledContent: FunctionComponent<PopoverContentProps> = ({ className, ...props }) => {
    const colorScheme = useColorSchemeScope();

    return (
        <Content
            className={clsx(
                floatingSurfaceClassNames.tone.gray,
                'font-sans rounded-md min-w-55 p-m z-1205 outline-none',
                'will-change-[transform,opacity]',
                'data-[side=bottom]:animate-slide-up-fade',
                'data-[side=top]:animate-slide-down-fade',
                'data-[side=right]:animate-slide-left-fade',
                'data-[side=left]:animate-slide-right-fade',
                className,
            )}
            data-color-scheme={colorScheme}
            {...props}
        />
    );
};

export const StyledTrigger: FunctionComponent<ComponentProps<typeof Trigger>> = ({ className, ...props }) => (
    <Trigger className={clsx('outline-none', className)} {...props} />
);

export const StyledClose: FunctionComponent<ComponentProps<typeof Close>> = ({ className, ...props }) => (
    <Close
        className={clsx(
            'inline-flex items-center justify-center h-[24px] w-[24px]',
            'bg-transparent border-none rounded-sm text-text-content-subtle text-meta',
            'cursor-pointer outline-none p-xs',
            'transition-all duration-100 ease-in-out',
            floatingSurfaceItemClassNames.hover,
            'focus:shadow-[0_0_0_2px_var(--semantic-border-focus)]',
            className,
        )}
        {...props}
    />
);

export const Popover: FunctionComponent<PopoverProps> = ({
    children,
    defaultOpen,
    modal,
    onOpenChange,
    open,
    ...props
}) => {
    return (
        <Root defaultOpen={defaultOpen} modal={modal} open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Root>
    );
};

export const PopoverTrigger: FunctionComponent<PopoverTriggerProps> = ({ asChild, children, className, ...props }) => {
    return (
        <StyledTrigger asChild={asChild} className={className} {...props}>
            {children}
        </StyledTrigger>
    );
};

export const PopoverAnchor: FunctionComponent<PopoverAnchorProps> = ({
    asChild,
    children,
    className,
    ref,
    ...props
}) => {
    return (
        <Anchor asChild={asChild} className={className} ref={ref} {...props}>
            {children}
        </Anchor>
    );
};

export const PopoverContent: FunctionComponent<PopoverContentProps> = ({ children, ...props }) => {
    return (
        <Portal>
            <StyledContent {...props}>{children}</StyledContent>
        </Portal>
    );
};

export const PopoverClose: FunctionComponent<PopoverCloseProps> = ({ asChild, children, className, ...props }) => {
    return (
        <StyledClose asChild={asChild} className={className} {...props}>
            {children ?? '×'}
        </StyledClose>
    );
};
